/// <amd-module name="Traceability/Common/Utilities" />
import { IArtifactDetails, IArtifactServerObject, IDataProviderParams, ITraceabilityLinkingService } from "Traceability/Common/Contracts";
import * as Resources from "Traceability/Common/Resources";
import { equals as stringEquals, localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
// Make sure ITraceabilityLinkingService is registered for FPS
import * as TraceabilityLinkingService from "Traceability/Common/Linking";

const PipelineArtifactTypeText: string = "pipeline";
const ACRArtifactTypeText: string = "AzureContainerRepository";
const RepositoryTypes: string[] = ["TfsGit", "GitHub", "Git"];
const hasImageDetailsDataProviderId: string = "ms.vss-environments-web.has-image-details-data-provider";

export class Utilities {
    public static getDataProviderParams(artifactDetails: IArtifactDetails): IDataProviderParams {
        return {
            artifactAlias: artifactDetails && artifactDetails.artifactVersion && artifactDetails.artifactVersion.alias || "",
            currentRunId: artifactDetails.currentRunId,
            useSnapshot: true
        };
    }

    public static getDataProviderDataAsync<T>(service: IVssContributionService, dataProviderId: string, artifactDetails: IArtifactDetails): Promise<T> {
        const params = Utilities.getDataProviderParams(artifactDetails)
        return service.getDataAsync<T>(dataProviderId, params, true) as Promise<T>;
    }

    public static getArtifactDetailsObject<T>(artifactData: IArtifactServerObject<T>, currentRunId: number): IArtifactDetails {
        const alias = artifactData.artifactVersion.alias;
        const isPrimary = localeIgnoreCaseComparer(alias, this._primaryArtifactAlias) === 0;
        if (isPrimary) {
            artifactData.artifactVersion.alias = Resources.CurrentPipelineText;
        }

        const details: IArtifactDetails = {
            artifactVersion: artifactData.artifactVersion,
            repository: artifactData.repository,
            currentRunId: currentRunId,
            isPrimary: isPrimary
        };

        return details;
    }

    public static fillArtifactVersionDisplayUri<T>(pageContext: IVssPageContext, artifactsData: IArtifactServerObject<T>[]): IArtifactServerObject<T>[] {
        const linkingService = pageContext.getService<ITraceabilityLinkingService>("ITraceabilityLinkingService");
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        (artifactsData || []).forEach(artifact => {
            const version = artifact && artifact.artifactVersion;
            // check for pipeline artifact type, similarly for ACR etc we will have links in future
            if (version && version.id && stringEquals(version.type || "", PipelineArtifactTypeText, true)) {
                artifact.artifactVersion.displayUri = linkingService.getRunUrl(pageContext, Number.parseInt(version.id), version.projectId);
            }
            else if (version && version.id && stringEquals(version.type || "", ACRArtifactTypeText, true)) {
                const imageUri = version.properties && version.properties["containerUri"] || "";
                if (!!imageUri) {
                    const imageName = "https://" + imageUri;
                    artifact.artifactVersion.displayUri = this._hasImageDetails(pageContext, imageName).then(data => {
                        if (data && data.hasImageDetails && data.hasImageDetails[imageName]) {
                            const params = { "imageUri": imageUri };
                            return historyService.generateUrl(params);
                        }
                        return "";
                    });
                }
            }
        });

        return artifactsData;
    }

    public static getArtifactCardHeaderText(artifactDetails: IArtifactDetails): string {
        const artifactVersion = artifactDetails && artifactDetails.artifactVersion || {};
        const { name, alias, type, id } = artifactVersion;
        // if primary, do not show the version in the title
        if (artifactDetails.isPrimary) {
            return alias;
        }

        let versionName: string;
        // is this repository type artifact
        const isRepoArtifact = RepositoryTypes.findIndex(repoType => stringEquals(repoType, type || "", true)) > -1;
        if (isRepoArtifact) {
            versionName = Utilities.getCommitShortId(name || id || "");
        }
        // is this pipeline type artifact
        else if (stringEquals(PipelineArtifactTypeText, type || "", true)) {
            versionName = name ? localeFormat("#{0}", name) : id || "";
        }
        else {
            versionName = name || id || "";
        }

        return versionName ? localeFormat("{0} / {1}", alias, versionName) : alias;
    }

    public static setPrimaryArtifactAliasName(primaryArtifactName: string): void {
        this._primaryArtifactAlias = primaryArtifactName;
    }

    public static getCommitShortId(commitId: string): string {
        return commitId ? commitId.substr(0, 9) : "";
    }

    private static _hasImageDetails(pageContext: IVssPageContext, imageName: string): Promise<{ hasImageDetails: { [key: string]: boolean } } | undefined> {
        const additionalProperties = { imageNames: [imageName] };
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<{ hasImageDetails: { [key: string]: boolean } }>(hasImageDetailsDataProviderId, additionalProperties);
    }

    private static _primaryArtifactAlias: string;
}
